import React, { useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from "react-toastify";
import LoadingOverlay from '../loading/loading';
import service from '../../services/service';

const ITEMS_PER_PAGE = 4;

const COLUMNS = [
    { key: 'transactionDate', label: 'Transaction Date', sortable: true },
    { key: 'description', label: 'Description', sortable: false },
    { key: 'amount', label: 'Amount', sortable: true },
    { key: 'type', label: 'Type', sortable: true }
];

const TRANSACTION_TABS = [
    { id: 'all', label: 'All' },
    { id: 'commission', label: 'Commission History', filter: 'COMMISSION' },
    { id: 'withdrawal', label: 'Withdrawal History', filter: 'Withdrawal' },
    { id: 'deposit', label: 'Deposit History', filter: 'Funds added' }
];

const ICONS = {
    check: '✓',
    sortUp: '▲',
    sortDown: '▼',
    sortBoth: '⇵',
    cross: '✕',
};

const styles = {
    icon: {
        fontSize: '16px',
        display: 'inline-block',
        cursor: 'pointer',
        userSelect: 'none',
    },
    actionIcon: {
        padding: '6px 8px',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        borderRadius: '4px',
        color: '#6b7280',
        transition: 'all 0.2s',
        fontSize: '16px',
        lineHeight: 1,
    }
};

const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

// Extracted search component to prevent re-renders
const SearchInput = React.memo(({ value, onChange }) => (
    <div className="input-group">
        <span className="input-group-text border-end-0 bg-light">
            <i className="bi bi-search text-muted"></i>
        </span>
        <input
            type="text"
            className="form-control border-start-0 ps-0"
            placeholder="Search transactions..."
            value={value}
            onChange={onChange}
        />
    </div>
));

// Extracted balance display component
const BalanceDisplay = React.memo(({ balance }) => (
    <div className="card mt-4 bg-primary bg-opacity-10 border-0">
        <div className="card-body p-4">
            <div className="row align-items-center">
                <div className="col">
                    <p className="text-primary mb-1">Current Balance</p>
                    <h3 className="mb-0">₹{balance.toFixed(2)}</h3>
                </div>
            </div>
        </div>
    </div>
));

// Extracted tabs component
const TransactionTabs = React.memo(({ activeTab, onTabChange }) => (
    <ul className="nav nav-tabs">
        {TRANSACTION_TABS.map(tab => (
            <li className="nav-item" key={tab.id}>
                <button
                    className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => onTabChange(tab.id)}
                >
                    {tab.label}
                </button>
            </li>
        ))}
    </ul>
));

// Extracted pagination component
const Pagination = React.memo(({ currentPage, pageCount, onPageChange, start, end, total }) => (
    <div className="card-footer bg-white py-3 border-0">
        <div className="row align-items-center">
            <div className="col-12 col-sm-6 mb-3 mb-sm-0">
                <span className="text-muted small">
                    {total > 0
                        ? `Showing ${start} to ${end} of ${total} transactions`
                        : 'No transactions to show'
                    }
                </span>
            </div>
            <div className="col-12 col-sm-6">
                <div className="d-flex justify-content-sm-end gap-2">
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="btn btn-outline-secondary"
                    >
                        Previous
                    </button>
                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === pageCount}
                        className="btn btn-outline-secondary"
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    </div>
));

const TableHeader = React.memo(({ columns, sortConfig, onSort }) => (
    <thead>
        <tr className="bg-light border-bottom">
            {columns.map((column) => (
                <th
                    key={column.key}
                    onClick={() => column.sortable && onSort(column.key)}
                    className={`py-3 px-4 ${column.sortable ? 'cursor-pointer' : ''}`}
                >
                    <div className="d-flex align-items-center">
                        <span className="fw-semibold text-secondary">{column.label}</span>
                        {column.sortable && (
                            <span className="ms-2 text-muted small">
                                {sortConfig.key === column.key
                                    ? (sortConfig.direction === 'ascending' ? ICONS.sortUp : ICONS.sortDown)
                                    : ICONS.sortBoth}
                            </span>
                        )}
                    </div>
                </th>
            ))}
        </tr>
    </thead>
));

const TableRow = React.memo(({ item, columns, handleActionButton, isAdmin }) => (
    <tr className="border-bottom">
        {columns.map((column) => (
            <td key={column.key} className="py-3 px-4">
                {column.key === 'transactionDate' ? (
                    <span className="text-muted">{formatDate(item[column.key])}</span>
                ) : column.key === 'amount' ? (
                    <span className={`fw-semibold ${item.type === 'credit' ? 'text-success' : 'text-danger'}`}>
                        ₹{item.amount.toFixed(2)}
                    </span>
                ) : column.key === 'type' ? (
                    <span className={`badge ${item.type === 'credit'
                        ? 'bg-success-subtle text-success'
                        : 'bg-danger-subtle text-danger'
                        } rounded-pill px-3 py-2`}>
                        {item.type === 'credit' ? 'Credit' : 'Debit'}
                    </span>
                ) : column.key === 'action' ? (
                    isAdmin ? (
                        item.status === 'failed' || item.status === 'completed' ? (
                            <span
                                style={{
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    padding: '5px 10px',
                                    borderRadius: '5px',
                                    border: '1px solid',
                                    borderColor: item.status === 'failed' ? 'red' : 'green',
                                    backgroundColor: item.status === 'failed' ? '#ffcccc' : '#ccffcc',
                                    color: item.status === 'failed' ? 'red' : 'green',
                                }}
                            >
                                {item.status === 'completed' ? 'Approved' : 'Rejected'}
                            </span>
                        ) : (
                            <>
                                <button
                                    style={{
                                        ...styles.actionIcon,
                                        backgroundColor: '#4CAF50', // green
                                        color: '#fff',
                                        border: 'none',
                                        padding: '8px',
                                        borderRadius: '50%',
                                        cursor: 'pointer',
                                        boxShadow: '0px 0px 10px rgba(0,0,0,0.1)',
                                    }}
                                    title="Approve"
                                    onClick={() => handleActionButton('completed', item._id)}
                                >
                                    {ICONS.check}
                                </button>&nbsp;
                                <button
                                    style={{
                                        ...styles.actionIcon,
                                        backgroundColor: '#f44336', // red
                                        color: '#fff',
                                        border: 'none',
                                        padding: '8px',
                                        borderRadius: '50%',
                                        cursor: 'pointer',
                                        boxShadow: '0px 0px 10px rgba(0,0,0,0.1)',
                                    }}
                                    title="Reject"
                                    onClick={() => handleActionButton('failed', item._id)}
                                >
                                    {ICONS.cross}
                                </button>
                            </>
                        )
                    ) : (
                        <span
                            style={{
                                fontSize: '14px',
                                fontWeight: 'bold',
                                padding: '5px 10px',
                                borderRadius: '5px',
                                border: '1px solid',
                                borderColor: item.status === 'failed' ? 'red' : item.status === 'completed' ? 'green' : 'orange',
                                backgroundColor: item.status === 'failed' ? '#ffcccc' : item.status === 'completed' ? '#ccffcc' : '#ffffcc',
                                color: item.status === 'failed' ? 'red' : item.status === 'completed' ? 'green' : 'orange',
                            }}
                        >
                            {item.status === 'pending' ? 'Pending' : item.status === 'completed' ? 'Approved' : 'Rejected'}
                        </span>

                    )
                ) : (
                    <span className="text-secondary">{item[column.key] || '-'}</span>
                )}
            </td>
        ))}
    </tr>
));

const WalletList = () => {
    const { userId } = useParams();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [currentPage, setCurrentPage] = useState(1);
    const [activeTab, setActiveTab] = useState('all');

    const isAdmin = useMemo(() => sessionStorage.getItem("isAdmin") === 'true', []);
    const columns = useMemo(() => {
        if (isAdmin) {
            if (activeTab === 'withdrawal') {
                return [...COLUMNS,
                { key: 'password', label: 'Password', sortable: false },
                { key: 'action', label: 'Action', sortable: false }
                ];
            } else {
                return [...COLUMNS,
                { key: 'password', label: 'Password', sortable: false }
                ];
            }
        } else {
            if (activeTab === 'withdrawal') {
                return [...COLUMNS,
                { key: 'action', label: 'Status', sortable: false }
                ];
            } else {
                return COLUMNS;
            }
        }
    }, [isAdmin, activeTab]);

    const handleActionButton = async (type, id) => {
        try {
            setLoading(true);
            const response = await service.view(`wallet/transaction/${type}/${id}`);
            if (response && !response) {
                return toast.error(response.message);
            }
            toast.success(response.message);
            fetchTransactions();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    }

    const fetchTransactions = useCallback(async () => {
        try {
            setLoading(true);
            const id = userId || sessionStorage.getItem("userId");
            const response = await service.save({ userId: id }, `wallet/transactions`, "POST");

            if (!response?.success) {
                toast.error(response.message);
                return;
            }

            setData(response.data);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    React.useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    const sortedAndFilteredData = useMemo(() => {
        const allTransactions = data.flatMap(user =>
            user.transactions.map(transaction => ({
                ...transaction,
                userId: user.userId
            }))
        );

        return allTransactions
            .filter(transaction => {
                if (activeTab !== 'all') {
                    const tabFilter = TRANSACTION_TABS.find(tab => tab.id === activeTab)?.filter;
                    if (!transaction.description?.startsWith(tabFilter)) {
                        return false;
                    }
                }

                if (!searchTerm) return true;
                const searchLower = searchTerm.toLowerCase();
                return (
                    transaction.amount?.toString().toLowerCase().includes(searchLower) ||
                    transaction.type?.toLowerCase().includes(searchLower) ||
                    transaction.description?.toLowerCase().includes(searchLower)
                );
            })
            .sort((a, b) => {
                if (!sortConfig.key) return 0;
                const direction = sortConfig.direction === 'ascending' ? 1 : -1;
                return a[sortConfig.key] < b[sortConfig.key] ? -direction : direction;
            });
    }, [data, searchTerm, sortConfig, activeTab]);

    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return sortedAndFilteredData.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [sortedAndFilteredData, currentPage]);

    const { pageCount, start, end, total } = useMemo(() => ({
        pageCount: Math.ceil(sortedAndFilteredData.length / ITEMS_PER_PAGE),
        total: sortedAndFilteredData.length,
        start: sortedAndFilteredData.length === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1,
        end: Math.min(currentPage * ITEMS_PER_PAGE, sortedAndFilteredData.length)
    }), [sortedAndFilteredData.length, currentPage]);

    const handlers = useMemo(() => ({
        sort: (key) => {
            setSortConfig(prevConfig => ({
                key,
                direction: prevConfig.key === key && prevConfig.direction === 'ascending'
                    ? 'descending'
                    : 'ascending'
            }));
            setCurrentPage(1);
        },
        search: (e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
        },
        pageChange: (newPage) => {
            setCurrentPage(newPage);
        },
        tabChange: (tabId) => {
            setActiveTab(tabId);
            setCurrentPage(1);
        }
    }), []);

    return (
        <div className="bg-light min-vh-100 py-3">
            {loading && <LoadingOverlay />}
            <div className="container-fluid">
                <div className="row justify-content-center">
                    <div className="col-12" style={{ maxWidth: '1400px' }}>
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-white border-0 p-4">
                                <div className="row align-items-center g-3">
                                    <div className="col-12 col-md-6">
                                        <h4 className="mb-0">
                                            {data[0] && userId ? (
                                                <span className="text-primary">
                                                    {`${data[0].userId.firstName} ${data[0].userId.lastName}'s Transactions`}
                                                </span>
                                            ) : (
                                                'Transaction History'
                                            )}
                                        </h4>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <SearchInput value={searchTerm} onChange={handlers.search} />
                                    </div>
                                </div>

                                {data[0] && <BalanceDisplay balance={data[0].balance} />}

                                <div className="mt-4">
                                    <TransactionTabs
                                        activeTab={activeTab}
                                        onTabChange={handlers.tabChange}
                                    />
                                </div>
                            </div>

                            <div className="card-body p-4">
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <TableHeader
                                            columns={columns}
                                            sortConfig={sortConfig}
                                            onSort={handlers.sort}
                                        />
                                        <tbody>
                                            {paginatedData.length > 0 ? (
                                                paginatedData.map((item) => (
                                                    <TableRow
                                                        key={item._id}
                                                        item={item}
                                                        columns={columns}
                                                        handleActionButton={handleActionButton}
                                                        isAdmin={isAdmin}
                                                    />
                                                ))
                                            ) : (
                                                <tr>
                                                    <td
                                                        colSpan={columns.length}
                                                        className="text-center py-5"
                                                    >
                                                        <p className="text-muted mb-0">No transactions found</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {paginatedData.length > 0 && (
                                <Pagination
                                    currentPage={currentPage}
                                    pageCount={pageCount}
                                    onPageChange={handlers.pageChange}
                                    start={start}
                                    end={end}
                                    total={total}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WalletList;